import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import Flex from '@/components/Flex';
import { Props as TextProps } from '@/components/text/StyledText';
import { Theme } from '@/styles/theme';
import { ColorProps, PaddingProps, SizeProps } from '@/types/ui';
import { getColorStyle, getSizeStyle } from '@/utils/style/ui';

interface StyleProps {
  isDisabled?: boolean;
  isReadonly?: boolean;
  height?: string | number;
  padding?: PaddingProps;
  fontSize?: SizeProps;
  fontWeight?: keyof Theme['font'];
  textAlign?: TextProps['$textAlign'];
  rounded?: keyof Theme['rounded'];
  color?: ColorProps;
  borderColor?: ColorProps;
  borderWidth?: number;
  bg?: keyof Theme['bg'];
  cursor?: string;
}

export interface Props extends StyleProps, PropsWithChildren {
  prefixNode?: React.ReactNode;
  suffixNode?: React.ReactNode;
}

export const Background = styled.div``;
export const Container = styled(Flex)``;
const Root = styled(Flex)<StyleProps>`
  position: relative;
  color: ${({ theme, color }) =>
    getColorStyle({
      theme,
      color,
    }) || theme.colors.text};
  font-size: ${({ theme, fontSize }) =>
    getSizeStyle({ theme, size: fontSize }) || theme.size.md};
  ${({ theme, fontWeight }) => theme.font[fontWeight ?? 'regular']};
  display: flex;
  align-items: center;
  text-align: ${({ textAlign }) => textAlign ?? 'left'};
  ${({ theme, rounded }) => theme.rounded[rounded ?? 'md']};
  overflow: hidden;

  & > *:not(${Container}),
  & > ${Container} > * {
    z-index: 1;
  }
  & > ${Container} > * {
    position: relative;
    width: 100%;
  }
  ${Background} {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    ${({ theme, rounded }) => theme.rounded[rounded ?? 'md']};
    border-style: solid;
    border-color: ${({ theme, borderColor }) =>
      getColorStyle({
        theme,
        color: borderColor,
      }) || theme.colors.stroke};
    border-width: ${({ borderWidth }) => borderWidth ?? 1}px;
    ${({ theme, bg }) => theme.bg[bg ?? 'surface']}
    ${({ isDisabled, cursor }) =>
      isDisabled ? 'cursor: default;' : cursor ? `cursor: ${cursor};` : ''}
    ${({ theme }) => theme.transition.default}
    z-index: 0;
  }
  &:hover ${Background} {
    ${({ theme, isDisabled, isReadonly }) =>
      isDisabled || isReadonly ? '' : `border-color: ${theme.colors.active};`}
    ${({ theme, isDisabled, isReadonly }) =>
      isDisabled || isReadonly ? '' : theme.bg.surfaceHover}
  }
  &:disabled {
    cursor: default;
    pointer-events: none;
  }
  ${({ isDisabled, isReadonly }) =>
    isDisabled || isReadonly
      ? `
          cursor: default;
        `
      : ''}
`;

export default function InputPanel({
  height = 48,
  padding = 'xs',
  prefixNode,
  suffixNode,
  children,
  ...props
}: Props & React.ComponentPropsWithoutRef<'div'>) {
  return (
    <Root $gap="xxs" {...props} $height={height} $padding={padding}>
      {prefixNode ? <div>{prefixNode}</div> : ''}
      <Container $direction="column" $justify="center" $grow={1}>
        {children}
        <Background />
      </Container>
      {suffixNode ? <div>{suffixNode}</div> : ''}
    </Root>
  );
}
